import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Database, Cpu, CheckSquare, FileText, UserCheck, BarChart3, ChevronRight } from "lucide-react";

export default function SaasExamEcosystem() {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      title: "Question Bank",
      desc: "Store millions of course questions categorised by subject, chapter, cognitive level, and past frequency.",
      icon: <Database size={20} />,
      color: "from-violet-500 to-purple-500",
      accent: "violet"
    },
    {
      title: "AI Exam Generator",
      desc: "Autoselect questions matching customized difficulty parameters and mark layouts in under 10 seconds.",
      icon: <Cpu size={20} />,
      color: "from-blue-500 to-indigo-500",
      accent: "blue"
    },
    {
      title: "MCQ & Speed Exams",
      desc: "Distribute online multiple-choice tests with integrated browser lockers and proctoring controls.",
      icon: <CheckSquare size={20} />,
      color: "from-cyan-500 to-blue-500",
      accent: "cyan"
    },
    {
      title: "Subjective Exams",
      desc: "Students upload hand-written answer scripts or draw directly on digital stylus canvas layouts.",
      icon: <FileText size={20} />,
      color: "from-purple-500 to-pink-500",
      accent: "purple"
    },
    {
      title: "Teacher Evaluation",
      desc: "Tutors review scripts with AI-drafted marking outlines and quick feedback tools.",
      icon: <UserCheck size={20} />,
      color: "from-emerald-500 to-teal-500",
      accent: "emerald"
    },
    {
      title: "Analytics & Results",
      desc: "Deliver instant rank scores, progress metrics, and parent alerts across the institution.",
      icon: <BarChart3 size={20} />,
      color: "from-rose-500 to-pink-500",
      accent: "rose"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [steps.length]);

  return (
    <section className="py-24 bg-slate-50 dark:bg-slate-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            The Digital Exam Ecosystem
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 font-medium">
            From seeding mock question banks to distributing reports, Skillzy digitizes the entire academic assessment workflow.
          </p>
        </div>

        {/* Workflow Diagram Grid */}
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Animated Step Visuals */}
          <div className="lg:col-span-6 space-y-4">
            {steps.map((step, idx) => {
              const isActive = activeStep === idx;
              return (
                <button
                  key={step.title}
                  onClick={() => setActiveStep(idx)}
                  className={`w-full flex items-start gap-4 p-5 rounded-2xl border text-left transition-all duration-300 ${
                    isActive
                      ? "bg-white dark:bg-slate-950 border-violet-500/40 shadow-lg scale-[1.02]"
                      : "bg-white/40 dark:bg-slate-950/20 border-slate-100 dark:border-slate-800 opacity-60 hover:opacity-100"
                  }`}
                >
                  {/* Step Icon */}
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${step.color} text-white flex items-center justify-center shrink-0 shadow-md`}>
                    {step.icon}
                  </div>
                  <div>
                    <h3 className="font-extrabold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                      <span>{step.title}</span>
                      {isActive && <span className="w-1.5 h-1.5 rounded-full bg-violet-600 animate-ping" />}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed font-semibold">
                      {step.desc}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right Column: Workflow Visualization Diagram */}
          <div className="lg:col-span-6 flex justify-center">
            <div className="bg-slate-950 rounded-[2.5rem] border border-slate-800 p-8 shadow-2xl w-full max-w-md aspect-square flex flex-col justify-between overflow-hidden relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-violet-600/10 rounded-full blur-[100px] pointer-events-none" />

              {/* Graphic title */}
              <div className="flex items-center justify-between border-b border-slate-850 pb-3">
                <span className="text-[10px] font-black uppercase text-slate-400">Exam Pipeline Audit</span>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-[9px] text-slate-500 font-bold">Secure API connected</span>
                </div>
              </div>

              {/* Node workflow visual representation */}
              <div className="flex-1 flex flex-col justify-center items-center py-6 relative">
                
                {/* Visual Connector Path */}
                <div className="absolute top-8 bottom-8 left-1/2 w-0.5 bg-dashed bg-slate-800" />

                {/* Nodes animation list */}
                <div className="space-y-6 w-full max-w-xs relative z-10">
                  {steps.map((step, idx) => {
                    const isActive = activeStep === idx;
                    return (
                      <motion.div
                        key={step.title}
                        animate={isActive ? { scale: 1.05 } : { scale: 1 }}
                        className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                          isActive
                            ? "bg-slate-900 border-violet-500/40 shadow-xl"
                            : "bg-slate-950/80 border-slate-850 opacity-40"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${step.color} text-white flex items-center justify-center shrink-0`}>
                            {step.icon}
                          </div>
                          <span className="text-[11px] font-black text-white">{step.title}</span>
                        </div>

                        {isActive ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[8px] font-mono text-violet-400">Processing...</span>
                            <ChevronRight size={10} className="text-violet-400 animate-bounce" />
                          </div>
                        ) : (
                          <span className="text-[8px] font-mono text-slate-600">Idle</span>
                        )}
                      </motion.div>
                    );
                  })}
                </div>

              </div>

              {/* Footer status log */}
              <div className="bg-slate-900 rounded-xl p-3 border border-slate-850 flex items-center gap-2">
                <span className="text-[9px] font-mono text-slate-400">
                  &gt; [System Log]: Step {activeStep + 1} pipeline connected. Execution latency 4ms.
                </span>
              </div>

            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
